import { useCallback, useEffect, useState } from 'react'

import {
  DARK_THEMES,
  isPreference,
  resolveTheme,
  THEME_STORAGE_KEY,
  ThemeContext,
  type Theme,
  type ThemePreference,
} from '@/hooks/useTheme'

/**
 * The theme, applied to `<html>` as `data-theme`.
 *
 * A preference, not view state: it follows the person across every page, so
 * it persists to storage rather than to the URL (CLAUDE.md §4.5). There is no
 * user-preferences endpoint yet, so storage is the only home it has.
 *
 * `system` — the starting choice — follows the device: the default theme in
 * light mode, the dark theme in dark mode, switching live when the device
 * does. `color-scheme` is written alongside, so native checkboxes and
 * scrollbars match the theme on screen.
 *
 * This does not handle first paint. The pre-paint script in `index.html`
 * applies the same choice before the stylesheet and the bundle load; this
 * keeps the DOM in step when it changes afterwards.
 */
function apply(theme: Theme) {
  const root = document.documentElement
  root.setAttribute('data-theme', theme)
  root.style.colorScheme = DARK_THEMES.has(theme) ? 'dark' : 'light'
}

function stored(): ThemePreference {
  try {
    const value = window.localStorage.getItem(THEME_STORAGE_KEY)
    return isPreference(value) ? value : 'system'
  } catch {
    // Private mode, or storage blocked: follow the device.
    return 'system'
  }
}

const DARK_QUERY = '(prefers-color-scheme: dark)'

function deviceIsDark() {
  return typeof window.matchMedia === 'function' && window.matchMedia(DARK_QUERY).matches
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [preference, setPreference] = useState<ThemePreference>(stored)
  const [prefersDark, setPrefersDark] = useState(deviceIsDark)
  const theme = resolveTheme(preference, prefersDark)

  // The device can change under a page left open — at dusk, say.
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return
    const query = window.matchMedia(DARK_QUERY)
    const onChange = (event: MediaQueryListEvent) => setPrefersDark(event.matches)
    query.addEventListener('change', onChange)
    return () => query.removeEventListener('change', onChange)
  }, [])

  useEffect(() => apply(theme), [theme])

  const setTheme = useCallback((next: ThemePreference) => {
    setPreference(next)
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, next)
    } catch {
      // Unpersisted beats unusable: the choice still applies for this session.
    }
  }, [])

  return <ThemeContext value={{ theme, preference, setTheme }}>{children}</ThemeContext>
}
